import {
  Avatar,
  createStyles,
  makeStyles,
  SvgIcon,
  Theme,
  Typography,
} from '@material-ui/core';
import classNames from 'classnames';
import React from 'react';
import Stripe from 'stripe';

import { ForwardIcon } from 'src/components/icons';
import {
  typography10RegularStyle,
  typography12MediumStyle,
  typography12RegularStyle,
  typography15MediumStyle,
} from 'src/legacy/components/Text';
import * as Colors from 'src/theme/colors';

interface PaymentMethodOptionStyleProps {
  mobileView?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(1.5, 2.5),
      border: `1px solid ${Colors.DividersAndCardBorders}`,
      marginTop: theme.spacing(2),
      maxWidth: 500,
      borderRadius: theme.shape.borderRadius,
      '&:hover': {
        cursor: 'pointer',
      },
    },
    iconContainer: ({ mobileView }: PaymentMethodOptionStyleProps) =>
      mobileView
        ? { backgroundColor: theme.palette.primary.main, height: 26, width: 26 }
        : {
            backgroundColor: theme.palette.primary.main,
            height: 40,
            width: 40,
          },
    feeSeperator: {
      borderRight: `1px solid ${Colors.DividersAndCardBorders}`,
    },
    paymentContainer: {
      paddingLeft: theme.spacing(2.5),
      paddingRight: theme.spacing(2.5),
      flexGrow: 1,
    },
    paymentMethodIcon: {
      fontSize: ({ mobileView }: PaymentMethodOptionStyleProps) =>
        mobileView ? '14px' : '20px',
    },
    feeContainer: ({ mobileView }: PaymentMethodOptionStyleProps) =>
      mobileView
        ? {
            paddingRight: theme.spacing(1),
            minWidth: '45px',
            textAlign: 'right',
          }
        : {
            paddingRight: theme.spacing(1),
            minWidth: '80px',
            textAlign: 'right',
          },
    titleText: ({ mobileView }: PaymentMethodOptionStyleProps) =>
      mobileView
        ? { ...typography12MediumStyle }
        : {
            ...typography15MediumStyle,
          },
    subTitleText: ({ mobileView }: PaymentMethodOptionStyleProps) =>
      mobileView
        ? { ...typography10RegularStyle, lineHeight: '12px' }
        : {
            ...typography12RegularStyle,
          },
  }),
);

export interface PaymentMethodOptionProps {
  title: string;
  description: string;
  feeAmount: string;
  Icon: typeof SvgIcon;
  onPaymentMethodOptionSelected: (
    createdSource?: Stripe.CustomerSource,
  ) => void;
  isMobileView?: boolean;
  previewMode?: boolean;
}

export const PaymentMethodOption: React.FC<PaymentMethodOptionProps> = ({
  title,
  description,
  feeAmount,
  Icon,
  onPaymentMethodOptionSelected,
  isMobileView = false,
  previewMode = false,
}) => {
  const classes = useStyles({ mobileView: isMobileView || previewMode });

  const handlePaymentMethodOptionSelect = () => {
    if (!previewMode) {
      onPaymentMethodOptionSelected();
    }
  };

  return (
    <div className={classes.root} onClick={handlePaymentMethodOptionSelect}>
      <Avatar className={classes.iconContainer}>
        <Icon fill="white" className={classes.paymentMethodIcon} />
      </Avatar>
      <div
        className={classNames(classes.paymentContainer, {
          [classes.feeSeperator]: Boolean(feeAmount),
        })}
      >
        <Typography className={classes.titleText}>{title}</Typography>
        <Typography
          className={classes.subTitleText}
          style={{ color: Colors.GraySmall }}
        >
          {description}
        </Typography>
      </div>
      {feeAmount && (
        <div className={classes.feeContainer}>
          <Typography
            className={classes.subTitleText}
            style={{ color: Colors.LightGray }}
          >
            Fee {feeAmount}
          </Typography>
        </div>
      )}
      <ForwardIcon style={{ fontSize: isMobileView ? 10 : 15 }} />
    </div>
  );
};
